import React, { useEffect, useState } from "react";
import { API } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

const TABS = ["Rubros", "Agentes", "Precios API", "Accesos", "Módulos", "Integraciones"];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export default function Settings() {
  const [tab, setTab] = useState("Rubros");

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Configuración</h1>
        <p className="page-subtitle">Ajustes generales de la plataforma</p>
      </div>
      <div className="page-body">
        <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid var(--border)" }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "8px 16px", fontSize: 14, fontFamily: "inherit",
              color: tab === t ? "var(--accent)" : "var(--text-secondary)",
              borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
              marginBottom: -1, transition: "color 0.12s",
            }}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Rubros"      && <IndustriesTab />}
        {tab === "Agentes"     && <AgentsTab />}
        {tab === "Precios API" && <PricingTab />}
        {tab === "Accesos"       && <AccessTab />}
        {tab === "Módulos"       && <ModulesTab />}
        {tab === "Integraciones" && <IntegrationsTab />}
      </div>
    </div>
  );
}

/* ─── Rubros Tab ─────────────────────────────────────────────────────────── */
function IndustriesTab() {
  const toast = useToast();
  const [industries, setIndustries] = useState([]);
  const [newIndustry, setNewIndustry] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/api/settings/industries")
      .then(r => setIndustries(r.data))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!newIndustry.trim()) return;
    try {
      const r = await API.post("/api/settings/industries", { name: newIndustry.trim() });
      if (r.data) setIndustries(prev => [...prev, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewIndustry("");
      toast("Rubro agregado", "success");
    } catch { toast("Error al agregar rubro", "error"); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    try {
      await API.delete(`/api/settings/industries/${id}`);
      setIndustries(prev => prev.filter(i => i.id !== id));
      toast("Rubro eliminado", "success");
    } catch { toast("Error al eliminar", "error"); }
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="card">
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Rubros de clientes</h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>
          Estos rubros aparecen en el selector al crear o editar un cliente.
        </p>
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <input className="input" placeholder="Nuevo rubro..." value={newIndustry}
            onChange={e => setNewIndustry(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()} />
          <button className="btn btn-primary" onClick={handleAdd}>Agregar</button>
        </div>
        {loading ? <div className="spinner" /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {industries.map(i => (
              <div key={i.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 14px", background: "var(--bg-elevated)", borderRadius: "var(--radius-md)",
              }}>
                <span style={{ fontSize: 14 }}>{i.name}</span>
                <button className="btn btn-ghost btn-sm" style={{ color: "var(--danger)" }}
                  onClick={() => handleDelete(i.id, i.name)}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Agent editor — shared between desktop and mobile ───────────────────── */
function AgentEditor({ selected, promptText, setPromptText, onSave, onReset, saving, resetting }) {
  if (!selected) return null;
  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, gap: 12 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
            {selected.icon} {selected.name}
          </h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>{selected.description}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button className="btn btn-secondary btn-sm" onClick={onReset} disabled={resetting}>
            {resetting ? <span className="spinner" style={{ width: 12, height: 12 }} /> : "Restaurar"}
          </button>
          <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
            {saving ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Guardando...</> : "Guardar"}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 12, padding: "10px 14px", background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>
        💡 Usá <code style={{ background: "var(--bg-overlay)", padding: "1px 6px", borderRadius: 4 }}>{"{{PROFILE}}"}</code> para el perfil del cliente y <code style={{ background: "var(--bg-overlay)", padding: "1px 6px", borderRadius: 4 }}>{"{{HISTORY}}"}</code> para el historial.
      </div>

      <textarea
        value={promptText}
        onChange={e => setPromptText(e.target.value)}
        className="textarea"
        style={{ minHeight: 320, fontFamily: "monospace", fontSize: 13, lineHeight: 1.6 }}
      />

      <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
        Última actualización: {selected.updated_at ? new Date(selected.updated_at).toLocaleString("es-AR") : "—"}
      </p>
    </div>
  );
}

/* ─── Agents Tab ─────────────────────────────────────────────────────────── */
function AgentsTab() {
  const toast = useToast();
  const isMobile = useIsMobile();
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [promptText, setPromptText] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    API.get("/api/agent-prompts")
      .then(r => {
        setAgents(r.data);
        if (r.data[0]) { setSelected(r.data[0]); setPromptText(r.data[0].system_prompt); }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (agent) => {
    setSelected(agent);
    setPromptText(agent.system_prompt);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await API.patch(`/api/agent-prompts/${selected.agent_id}`, { system_prompt: promptText });
      setAgents(prev => prev.map(a => a.agent_id === selected.agent_id ? r.data : a));
      setSelected(r.data);
      toast("Prompt actualizado", "success");
    } catch { toast("Error al guardar", "error"); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!selected || !window.confirm("¿Restaurar el prompt original de este agente?")) return;
    setResetting(true);
    try {
      const r = await API.post(`/api/agent-prompts/${selected.agent_id}/reset`);
      setAgents(prev => prev.map(a => a.agent_id === selected.agent_id ? r.data : a));
      setSelected(r.data);
      setPromptText(r.data.system_prompt);
      toast("Prompt restaurado al original", "success");
    } catch { toast("Error al restaurar", "error"); }
    finally { setResetting(false); }
  };

  if (loading) return <div className="spinner" />;

  /* ── Mobile: accordion ── */
  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {agents.map(agent => {
          const isOpen = expandedId === agent.agent_id;
          return (
            <div key={agent.agent_id} style={{
              border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
              overflow: "hidden", background: "var(--bg-surface)",
            }}>
              {/* Accordion header */}
              <button
                onClick={() => {
                  if (isOpen) {
                    setExpandedId(null);
                  } else {
                    setExpandedId(agent.agent_id);
                    handleSelect(agent);
                  }
                }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "12px 16px", background: "none", border: "none",
                  cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                  color: isOpen ? "var(--accent)" : "var(--text-primary)",
                  background: isOpen ? "var(--accent-dim)" : "transparent",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 16 }}>{agent.icon}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{agent.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{agent.description}</div>
                  </div>
                </div>
                <span style={{ fontSize: 12, color: "var(--text-muted)", flexShrink: 0, marginLeft: 8 }}>
                  {isOpen ? "▲" : "▼"}
                </span>
              </button>

              {/* Accordion body */}
              {isOpen && selected?.agent_id === agent.agent_id && (
                <div style={{ padding: "0 12px 12px" }}>
                  <AgentEditor
                    selected={selected}
                    promptText={promptText}
                    setPromptText={setPromptText}
                    onSave={handleSave}
                    onReset={handleReset}
                    saving={saving}
                    resetting={resetting}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Desktop: 2-column grid ── */
  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 20, alignItems: "start" }}>
      <div className="card" style={{ padding: 8 }}>
        {agents.map(agent => (
          <button key={agent.agent_id} onClick={() => handleSelect(agent)} style={{
            display: "flex", alignItems: "center", gap: 10, width: "100%",
            padding: "10px 12px", borderRadius: "var(--radius-md)", border: "none",
            background: selected?.agent_id === agent.agent_id ? "var(--accent-dim)" : "none",
            color: selected?.agent_id === agent.agent_id ? "var(--accent)" : "var(--text-secondary)",
            cursor: "pointer", textAlign: "left", fontFamily: "inherit", fontSize: 14,
            fontWeight: selected?.agent_id === agent.agent_id ? 500 : 400,
            transition: "background 0.12s, color 0.12s",
          }}>
            <span>{agent.icon}</span>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{agent.name}</span>
          </button>
        ))}
      </div>

      <AgentEditor
        selected={selected}
        promptText={promptText}
        setPromptText={setPromptText}
        onSave={handleSave}
        onReset={handleReset}
        saving={saving}
        resetting={resetting}
      />
    </div>
  );
}

/* ─── Pricing Tab ────────────────────────────────────────────────────────── */
function PricingTab() {
  const toast = useToast();
  const [form, setForm] = useState({ input_per_m: "", output_per_m: "", global_monthly_limit_usd: "" });
  const [spend, setSpend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/api/settings/pricing")
      .then(r => setForm({
        input_per_m: r.data.input_per_m,
        output_per_m: r.data.output_per_m,
        global_monthly_limit_usd: r.data.global_monthly_limit_usd,
      }))
      .finally(() => setLoading(false));
    API.get("/api/settings/global-spend").then(r => setSpend(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await API.patch("/api/settings/pricing", {
        input_per_m:  parseFloat(form.input_per_m),
        output_per_m: parseFloat(form.output_per_m),
        global_monthly_limit_usd: parseFloat(form.global_monthly_limit_usd),
      });
      setForm({
        input_per_m: r.data.input_per_m,
        output_per_m: r.data.output_per_m,
        global_monthly_limit_usd: r.data.global_monthly_limit_usd,
      });
      setSpend(prev => prev ? { ...prev, limit_usd: r.data.global_monthly_limit_usd } : prev);
      toast("Precios actualizados", "success");
    } catch (err) { toast(err.response?.data?.error || "Error al guardar", "error"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div style={{ maxWidth: 480 }}>
      <div className="card">
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Precios de la API de Claude</h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.6 }}>
          Estos valores se usan para calcular el costo estimado en USD en la pantalla de Consumo.
          Actualizalos si Anthropic cambia sus precios.{" "}
          <a href="https://www.anthropic.com/pricing" target="_blank" rel="noreferrer"
            style={{ color: "var(--accent)" }}>
            Ver precios actuales →
          </a>
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="label">Tokens de entrada (por millón)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 14, color: "var(--text-muted)" }}>$</span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={form.input_per_m}
                onChange={e => setForm(p => ({ ...p, input_per_m: e.target.value }))}
                placeholder="3.00"
              />
            </div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="label">Tokens de salida (por millón)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 14, color: "var(--text-muted)" }}>$</span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={form.output_per_m}
                onChange={e => setForm(p => ({ ...p, output_per_m: e.target.value }))}
                placeholder="15.00"
              />
            </div>
          </div>
        </div>

        <div style={{ padding: "10px 14px", background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>
          💡 Ejemplo con estos valores: 1M tokens entrada = <strong style={{ color: "var(--text-secondary)" }}>${Number(form.input_per_m || 0).toFixed(2)}</strong> · 1M tokens salida = <strong style={{ color: "var(--text-secondary)" }}>${Number(form.output_per_m || 0).toFixed(2)}</strong>
        </div>

        <div className="field" style={{ marginBottom: 20, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
          <label className="label">Límite mensual global de consumo (USD)</label>
          <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.6 }}>
            Tope de gasto en USD por mes, sumando el consumo de todos los clientes y agentes juntos.
            Al alcanzarlo, los usuarios ya no van a poder usar ningún agente hasta que lo amplíes o
            empiece el mes siguiente — van a ver un aviso pidiéndoles que contacten a soporte.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 6, maxWidth: 200 }}>
            <span style={{ fontSize: 14, color: "var(--text-muted)" }}>$</span>
            <input
              className="input"
              type="number"
              min="0"
              step="1"
              value={form.global_monthly_limit_usd}
              onChange={e => setForm(p => ({ ...p, global_monthly_limit_usd: e.target.value }))}
              placeholder="100"
            />
          </div>

          {spend && (() => {
            const limit = parseFloat(spend.limit_usd) || 0;
            const current = parseFloat(spend.current_usd) || 0;
            const pct = limit > 0 ? Math.min(100, (current / limit) * 100) : 0;
            const isNearLimit = pct >= 80;
            return (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: "var(--text-muted)" }}>Consumo de este mes</span>
                  <span style={{ color: isNearLimit ? "var(--danger)" : "var(--text-secondary)", fontWeight: 600 }}>
                    ${current.toFixed(2)} / ${limit.toFixed(2)}
                  </span>
                </div>
                <div style={{ height: 6, borderRadius: 99, background: "var(--bg-elevated)", overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${pct}%`, borderRadius: 99,
                    background: isNearLimit ? "var(--danger)" : "var(--accent)",
                    transition: "width 0.3s",
                  }} />
                </div>
              </div>
            );
          })()}
        </div>

        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Guardando...</> : "Guardar precios"}
        </button>
      </div>
    </div>
  );
}

/* ─── Access Tab ─────────────────────────────────────────────────────────── */
function AccessTab() {
  const toast = useToast();
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/api/settings/allowed-emails")
      .then(r => setEmails(r.data))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    try {
      const r = await API.post("/api/settings/allowed-emails", { email });
      setEmails(r.data);
      setNewEmail("");
      toast("Email agregado", "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al agregar", "error");
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`¿Quitar acceso a "${email}"?`)) return;
    try {
      const r = await API.delete(`/api/settings/allowed-emails/${encodeURIComponent(email)}`);
      setEmails(r.data);
      toast("Email eliminado", "success");
    } catch { toast("Error al eliminar", "error"); }
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="card">
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Accesos externos</h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.6 }}>
          Emails que pueden iniciar sesión aunque no tengan cuenta <strong>@creativedog.agency</strong>.
          Ideal para freelancers o colaboradores externos.
        </p>
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <input
            className="input"
            type="email"
            placeholder="email@ejemplo.com"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
          />
          <button className="btn btn-primary" onClick={handleAdd}>Agregar</button>
        </div>
        {loading ? <div className="spinner" /> : emails.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>No hay accesos externos configurados.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {emails.map(email => (
              <div key={email} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 14px", background: "var(--bg-elevated)", borderRadius: "var(--radius-md)",
              }}>
                <span style={{ fontSize: 14 }}>{email}</span>
                <button className="btn btn-ghost btn-sm" style={{ color: "var(--danger)" }}
                  onClick={() => handleDelete(email)}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Modules Tab ────────────────────────────────────────────────────────── */
function ModulesTab() {
  const toast = useToast();
  const [modules, setModules] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingTo, setAddingTo] = useState(null); // module_name being edited
  const [selectedUserId, setSelectedUserId] = useState("");

  useEffect(() => {
    Promise.all([
      API.get("/api/module-permissions"),
      API.get("/api/clients/meta/assignable-users"),
    ])
      .then(([modsRes, usersRes]) => {
        setModules(modsRes.data);
        setAllUsers(usersRes.data);
      })
      .catch(err => toast(err.response?.data?.error || "Error al cargar", "error"))
      .finally(() => setLoading(false));
  }, []);

  const handleGrant = async (moduleName) => {
    if (!selectedUserId) return;
    try {
      await API.post("/api/module-permissions", { module_name: moduleName, user_id: parseInt(selectedUserId) });
      const user = allUsers.find(u => u.id === parseInt(selectedUserId));
      setModules(prev => prev.map(m =>
        m.name === moduleName
          ? { ...m, users: [...m.users, { id: user.id, name: user.name, email: user.email, avatar: user.avatar, role: user.role }] }
          : m
      ));
      setSelectedUserId("");
      setAddingTo(null);
      toast("Acceso otorgado", "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al otorgar acceso", "error");
    }
  };

  const handleRevoke = async (moduleName, userId) => {
    if (!window.confirm("¿Revocar acceso a este módulo?")) return;
    try {
      await API.delete(`/api/module-permissions/${moduleName}/${userId}`);
      setModules(prev => prev.map(m =>
        m.name === moduleName
          ? { ...m, users: m.users.filter(u => u.id !== userId) }
          : m
      ));
      toast("Acceso revocado", "success");
    } catch { toast("Error al revocar", "error"); }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div style={{ maxWidth: 620 }}>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.6 }}>
        Controlá qué usuarios tienen acceso a cada módulo adicional de la plataforma.
        Los módulos son independientes del rol del usuario.
      </p>
      {modules.map(mod => {
        const grantedIds = new Set(mod.users.map(u => u.id));
        const available = allUsers.filter(u => !grantedIds.has(u.id));
        return (
          <div key={mod.name} className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ fontSize: 20 }}>{mod.icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{mod.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{mod.name}</div>
              </div>
              <div style={{ marginLeft: "auto" }}>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                  background: "var(--bg-elevated)", color: "var(--text-muted)", border: "1px solid var(--border)",
                }}>
                  {mod.users.length} usuario{mod.users.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>

            {/* Current users */}
            {mod.users.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                {mod.users.map(u => (
                  <div key={u.id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 12px", background: "var(--bg-elevated)",
                    borderRadius: "var(--radius-md)", border: "1px solid var(--border)",
                  }}>
                    {u.avatar && <img src={u.avatar} alt={u.name} style={{ width: 26, height: 26, borderRadius: "50%" }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{u.email}</div>
                    </div>
                    <button className="btn btn-ghost btn-sm" style={{ color: "var(--danger)" }}
                      onClick={() => handleRevoke(mod.name, u.id)}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Add user */}
            {addingTo === mod.name ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select className="select" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                  style={{ flex: 1 }}>
                  <option value="">Seleccioná un usuario...</option>
                  {available.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
                <button className="btn btn-primary btn-sm" onClick={() => handleGrant(mod.name)}
                  disabled={!selectedUserId}>Dar acceso</button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setAddingTo(null); setSelectedUserId(""); }}>✕</button>
              </div>
            ) : (
              <button className="btn btn-secondary btn-sm" onClick={() => { setAddingTo(mod.name); setSelectedUserId(""); }}
                disabled={available.length === 0}>
                {available.length === 0 ? "Todos tienen acceso" : "+ Agregar usuario"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Integrations Tab ───────────────────────────────────────────────────── */
function IntegrationsTab() {
  const toast = useToast();
  const [channel, setChannel] = useState("#semaforo-comercial");
  const [tokenConfigured, setTokenConfigured] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingToken, setSavingToken] = useState(false);

  useEffect(() => {
    API.get("/api/settings/integrations")
      .then(r => {
        setChannel(r.data.slack_semaforo_channel || "#semaforo-comercial");
        setTokenConfigured(r.data.token_configured || false);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSaveChannel = async () => {
    setSaving(true);
    try {
      const r = await API.patch("/api/settings/integrations", { slack_semaforo_channel: channel });
      setChannel(r.data.slack_semaforo_channel);
      toast("Canal guardado", "success");
    } catch { toast("Error al guardar", "error"); }
    finally { setSaving(false); }
  };

  const handleSaveToken = async () => {
    if (!newToken.trim()) return;
    setSavingToken(true);
    try {
      await API.patch("/api/settings/integrations", { slack_bot_token: newToken.trim() });
      setTokenConfigured(true);
      setNewToken("");
      toast("Token guardado", "success");
    } catch { toast("Error al guardar el token", "error"); }
    finally { setSavingToken(false); }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div style={{ maxWidth: 520, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Bot Token */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 20 }}>🔑</span>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>Slack Bot Token</h3>
        </div>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16, lineHeight: 1.6 }}>
          Token del bot de Slack (<code>xoxb-...</code>). Necesario para publicar el Semáforo en Slack.
          El valor nunca se muestra una vez guardado.
        </p>
        {tokenConfigured && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: "var(--radius-md)", marginBottom: 14, fontSize: 13 }}>
            <span>✅</span>
            <span style={{ color: "var(--success)" }}>Token configurado</span>
          </div>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <input
            className="input"
            type="password"
            value={newToken}
            onChange={e => setNewToken(e.target.value)}
            placeholder={tokenConfigured ? "Ingresar nuevo token para reemplazar" : "xoxb-..."}
            onKeyDown={e => e.key === "Enter" && handleSaveToken()}
          />
          <button className="btn btn-primary" onClick={handleSaveToken} disabled={savingToken || !newToken.trim()}>
            {savingToken ? "Guardando..." : tokenConfigured ? "Reemplazar" : "Guardar"}
          </button>
        </div>
      </div>

      {/* Canal */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 20 }}>📣</span>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>Canal del Semáforo</h3>
        </div>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16, lineHeight: 1.6 }}>
          Canal donde Smith publica el reporte semanal. El bot debe estar invitado a ese canal.
        </p>
        <div style={{ marginBottom: 16 }}>
          <label className="label">ID del canal de Slack</label>
          <input
            className="input"
            value={channel}
            onChange={e => setChannel(e.target.value)}
            placeholder="C0123456789"
            style={{ maxWidth: 280 }}
          />
          <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
            Usá el ID del canal, no el nombre. En Slack: click derecho en el canal → <em>Ver detalles del canal</em> → copiá el ID al final (ej: <code>C0123456789</code>).
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleSaveChannel} disabled={saving}>
          {saving ? "Guardando..." : "Guardar canal"}
        </button>
      </div>
    </div>
  );
}
